import type { ReactNode } from 'react';
import React from 'react';
import { type Block, BLOCKS, type Inline, INLINES, type Text } from '@contentful/rich-text-types';
import { Heading, Hr, Image, Picture, TextLink, TextList, TextPassage } from '@petsmart-ui/sparky';
import type { Entry, Image as ImageType, ParseOptionsParameter } from '../../types';
import { useEntryHyperlink } from '../../hooks';
import { getImageProps } from '../propsImagesItems';
import './richTextParserOptions.scss';

export function getCombinedTextValueOfChildTextNodes(nodes: (Block | Inline | Text)[]) {
    return nodes.reduce((result, node) => {
        if (node.nodeType === 'text') {
            return result + node.value;
        }
        return result;
    }, '');
}

// [HCP-141]: Is there a need to strip special characters?
export function createDOMElementIdFromString(value: string) {
    return value.split(' ').join('-');
}

export const richTextParserOptions: ParseOptionsParameter = {
    renderText: text =>
        text.split('\n').reduce<React.ReactNode[]>((acc, segment, index) => {
            const segmentId = `${segment}-${index}`; // Combine segment content with index
            if (index === 0) return [segment];
            return [...acc, <br key={segmentId} />, segment];
        }, []),
    renderNode: {
        [BLOCKS.PARAGRAPH]: (_node, children) => <TextPassage className="text-center">{children}</TextPassage>,
        [BLOCKS.QUOTE]: (_node, children) => <TextPassage>{children}</TextPassage>,
        [BLOCKS.HEADING_1]: (node, children) => <Heading tagName="h1">{children}</Heading>,
        [BLOCKS.HEADING_2]: (node, children) => {
            const nodeText = getCombinedTextValueOfChildTextNodes(node.content);
            const elementId = createDOMElementIdFromString(nodeText);
            return (
                <Heading tagName="h2" id={elementId}>
                    {children}
                </Heading>
            );
        },
        [BLOCKS.HEADING_3]: (node, children) => <Heading tagName="h3">{children}</Heading>,
        [BLOCKS.HEADING_4]: (node, children) => <Heading tagName="h4">{children}</Heading>,
        [BLOCKS.HEADING_5]: (node, children) => <Heading tagName="h5">{children}</Heading>,
        [BLOCKS.HEADING_6]: (node, children) => <Heading tagName="h6">{children}</Heading>,
        [BLOCKS.HR]: () => <Hr />,
        [BLOCKS.LIST_ITEM]: (node, children) => <TextList.Item>{children}</TextList.Item>,
        [BLOCKS.OL_LIST]: (node, children) => (
            <TextList tagName="ol" variant="spaced">
                {children}
            </TextList>
        ),
        [BLOCKS.UL_LIST]: (node: Block | Inline | Text, children: ReactNode) => {
            return (
                <TextList tagName="ul" variant="spaced" className="rich-text__unordered-list ">
                    {children}
                </TextList>
            );
        },
        [BLOCKS.EMBEDDED_ASSET]: node => {
            const {
                data: {
                    target: { fields },
                },
            } = node;

            if (!fields?.title || !fields?.file?.url) {
                return null;
            }

            const { title, file } = fields;

            return (
                <Image
                    src={`https:${file.url}`}
                    width={file.details?.image?.width ?? undefined}
                    height={file.details?.image?.height ?? undefined}
                    alt={title}
                />
            );
        },
        [INLINES.ENTRY_HYPERLINK]: (node, children) => {
            const entry = useEntryHyperlink(node?.data?.target);

            if (!entry) {
                return null;
            }

            const { href } = entry;

            return <TextLink href={href}>{children}</TextLink>;
        },
        [INLINES.HYPERLINK]: (node, children) => {
            const { uri } = node.data;
            if (!uri) {
                return null;
            }

            const shouldOpenInNewTab = uri.includes('?newTab=true') || uri.includes('&newTab=true');

            const cleanUri = uri.replace('?newTab=true', '').replace('&newTab=true', '');

            return (
                <TextLink href={cleanUri} target={shouldOpenInNewTab ? '_blank' : undefined}>
                    {children}
                </TextLink>
            );
        },
        [BLOCKS.EMBEDDED_ENTRY]: node => {
            if (node.data.target.sys.contentType.sys.id === 'image') {
                const imageEntryFeilds = node.data.target.fields as ImageType;
                return <Picture {...getImageProps(imageEntryFeilds)} />;
            }

            const { actionText } = node.data.target.fields;
            const tags = node?.data?.target?.metadata.tags;
            const id = tags?.find((element: Entry) => element?.sys?.id)?.sys.id || '';
            return (
                <TextPassage className="text-center" id={id}>
                    {actionText}
                </TextPassage>
            );
        },
    },
};
